/**
 * types.ts
 * All shared TypeScript interfaces and enums for Future Beacon.
 */

// ── Geometry ───────────────────────────────────────────────────────────
export interface GridPosition {
  col: number;
  row: number;
}

export interface WorldPosition {
  x: number;
  y: number;
}

// ── Resources ──────────────────────────────────────────────────────────
export interface ResourceState {
  reputation: number;
  fanbase: number; // current population cap usage
  fanbaseCap: number;
  sales: number;
  gear: number;
  /** Earned progression memory — spending resources does not lower these. */
  peakReputation?: number;
  peakFanbase?: number;
  sceneScore?: number;
}

export type ResourceType = 'reputation' | 'fanbase' | 'sales' | 'gear';

// ── Terrain & fog ──────────────────────────────────────────────────────
export enum TerrainType {
  Grass = 'grass',
  Path = 'path',
  Water = 'water',
  Corporate = 'corporate',
  Building = 'building',
  Obstacle = 'obstacle', // impassable environmental debris, rubble, walls
  Locked = 'locked', // not yet unlocked / revealed area
}

export enum FogState {
  Unexplored = 0, // black
  Explored = 1, // greyed, static snapshot
  Visible = 2, // live
}

/** State of a claimable venue node on the map. */
export type VenueState = 'unclaimed' | 'claiming' | 'claimed';

// ── Factions ───────────────────────────────────────────────────────────
export enum Faction {
  Player = 'player',
  Enemy = 'enemy',
  Neutral = 'neutral',
}

// ── Band member / unit class identifiers ───────────────────────────────
export type BandClass =
  | 'protector'  // Steve & The Guitar Gang
  | 'promoter'   // Ali & The Vocal Mob
  | 'engine'     // Chris & The Rhythm Crew
  | 'sustain'    // Alex & The Bass Line
  | 'mystic'     // Jim & The Keyboard Prophets
  | 'songwriter' // Nigel & The Song Catchers — conquer/defend; broadcasts future verses
  | 'source'     // Nigel — inside the Beacon (non-deployable marker)
  | 'fan';       // Venue fans — spawned when a venue is claimed; can claim further venues for free

export type EnemyType =
  | 'StreamingMogul'
  | 'GenZSwarm'
  | 'BadManager'
  | 'TributeAct'
  | 'MusicPress'
  | 'FilePirate'
  | 'RivalVenueBuilder';

export type SpawnEdge = 'north' | 'south' | 'east' | 'west';

export type BuildingType =
  | 'FutureBeacon'
  | 'FlyerPost'
  | 'MerchStall'
  | 'Pub'
  | 'RehearsalSpace'
  | 'RecordingStudio'
  | 'SoundRig'
  | 'CommunityRadioMast';

export interface PlacedBuilding {
  id:                   string;
  type:                 BuildingType;
  col:                  number;
  row:                  number;
  constructionProgress: number;   // 0–1
  constructed:          boolean;
  hp:                   number;
  maxHp:                number;
  attackCooldown:       number;   // Sound Rig only
}

// ── Combat ─────────────────────────────────────────────────────────────
export interface CombatStats {
  maxHp: number;
  hp: number;
  damage: number;
  attackRate: number; // attacks per second
  attackRadius: number; // tiles
  sightRadius: number; // tiles
  moveSpeed: number; // tiles per second (pre-tempo modifier)
}

export type CombatOrder = 'idle' | 'move' | 'attackMove' | 'attackTarget' | 'defend';

// ── Waves (§9.6) ───────────────────────────────────────────────────────
export interface WaveEnemySpec {
  type: EnemyType;
  count: number;
  spawnEdge: SpawnEdge;
}

export interface WaveDefinition {
  delay: number; // seconds after level start
  enemies: WaveEnemySpec[];
  warningTime: number; // seconds advance notice if Prophecy active
}

// ── Enemy battle planner / Codex command contract ─────────────────────
export interface BattleUnitSnapshot {
  id: string;
  type: EnemyType | BandClass | string;
  col: number;
  row: number;
  hp: number;
  maxHp?: number;
  visibleToPlayer?: boolean;
  isMining?: boolean;
}

export interface BattleBuildingSnapshot {
  id: string;
  type: BuildingType | string;
  col: number;
  row: number;
  hp: number;
  maxHp?: number;
  level?: number;
  age?: number;
  spawnPressure?: number;
}

export interface BattleSnapshot {
  tick: number;
  wave: number;
  map?: { width: number; height: number };
  beacon: {
    signal: number;
    hp: number;
    captureProgress?: number;
    songDropProgress?: number;
  };
  resources: {
    enemyBudget: number;
    reputation?: number;
    sales?: number;
    gear?: number;
    fanbase?: number;
    fanbaseCap?: number;
  };
  visibility?: {
    /** 0.0 = unseen by player, 1.0 = heavily watched by player sight/fog. */
    edgeVisibility?: Partial<Record<SpawnEdge, number>>;
  };
  enemyUnits: BattleUnitSnapshot[];
  playerUnits: BattleUnitSnapshot[];
  buildings: BattleBuildingSnapshot[];
  venues?: Array<BattleBuildingSnapshot & { state?: VenueState; progress?: number }>;
  buildSites?: Array<{
    id: string;
    name?: string;
    col: number;
    row: number;
    gearRequired: number;
    gearProgress: number;
    fanCount?: number;
    active?: boolean;
    completed?: boolean;
  }>;
  gearSites?: Array<{
    id: string;
    col: number;
    row: number;
    active?: boolean;
    fanCount?: number;
  }>;
}

export type EnemyCommand =
  | { type: 'move'; unitId: string; target: GridPosition }
  | { type: 'spawn'; enemyType: EnemyType; count: number; fromEdge: SpawnEdge }
  | { type: 'ability'; unitId: string; abilityId: string; target?: GridPosition | { id: string } }
  | { type: 'priority'; targetId: string; priority: 'high' | 'low' }
  | { type: 'retreat'; unitId: string };

// ── Spells (§7) ────────────────────────────────────────────────────────
export type SpellId =
  | 'signalBoost'
  | 'temporalEcho'
  | 'crowdConfusion'
  | 'feedbackLoop'
  | 'prophecyReveal'
  | 'basslineSummon';

export interface SpellDefinition {
  id: SpellId;
  name: string;
  cooldown: number; // seconds
  reputationCost: number;
  description: string;
}

// ── Campaign / levels ──────────────────────────────────────────────────
export interface LevelDefinition {
  id: string;
  name: string;
  description: string;
  waves: WaveDefinition[];
}

// ── Persisted save state (§9.7) ────────────────────────────────────────
export interface SavedEntity {
  id: string;
  kind: string;
  position: WorldPosition;
  hp?: number;
}

export interface GameState {
  version: number;
  campaignLevel: string;
  waveIndex: number;
  resources: ResourceState;
  tempo: number;
  signalStrength: number;
  units: SavedEntity[];
  buildings: SavedEntity[];
  fog: number[]; // flattened FogState grid
  savedAt: number; // epoch ms
}

// ── Map generation output ─────────────────────────────────────────────────

/** Position of one randomly-placed venue on the map. */
export interface VenueLayout {
  /** Building footprint tile (marked as TerrainType.Building). */
  buildingCol: number;
  buildingRow: number;
  /** Adjacent passable tile — where a unit stands to claim the venue. */
  accessCol: number;
  accessRow: number;
}

/** Hidden enemy factory estate generated off the main town landmass. */
export interface CorporateHQLayout extends VenueLayout {
  /** Passable tiles around the HQ where manufactured enemy units can emerge. */
  spawnTiles: GridPosition[];
}

// ── DB-driven content (§ data layer) ────────────────────────────────────
// Tunable balance numbers that can be overridden from the database at load,
// falling back to the bundled defaults (seeded from constants.ts).
export interface BalanceValues {
  startingReputation: number;
  startingFanbase: number;
  startingSales: number;
  startingGear: number;
  beaconStartingSignal: number;
  songDropThreshold: number;
  tempoDefault: number;
  unitAttackRate: number;
  unitSightRadius: number;
  unitAttackRadius: number;
  // Per-class HP — ADMIN: unitHpProtector | unitHpPromoter | unitHpEngine | unitHpSustain | unitHpMystic | unitHpSongwriter
  unitHpProtector:  number;
  unitHpPromoter:   number;
  unitHpEngine:     number;
  unitHpSustain:    number;
  unitHpMystic:     number;
  unitHpSongwriter: number;
  // Move speeds — ADMIN: unitMoveSpeed | unitMoveSpeedPromoter | unitMoveSpeedMystic | unitMoveSpeedSongwriter
  unitMoveSpeed:            number;
  unitMoveSpeedPromoter:    number;
  unitMoveSpeedMystic:      number;
  unitMoveSpeedSongwriter:  number;
  // Rhythm — ADMIN: tempoDecayRate | tempoEnemyHit
  tempoDecayRate: number;
  tempoEnemyHit: number;

  // Passive income — ADMIN: resRepBaseIncome | resSalesBaseIncome | resSalesFanMult
  resRepBaseIncome:   number;
  resSalesBaseIncome: number;
  resSalesFanMult:    number;

  // Kill rewards — ADMIN: killRepSwarm | killSalesSwarm | killRepMogul | killSalesMogul
  killRepSwarm:   number;
  killSalesSwarm: number;
  killRepMogul:   number;
  killSalesMogul: number;

  // Venue incomes — ADMIN: venueRep/Sales/Fan per venue
  venueRepDirtyDuck:    number;
  venueSalesDirtyDuck:  number;
  venueSalesRecordShop: number;
  venueFanHall:         number;
  venueRepIndie:        number;
  venueSalesIndie:      number;
  // Outer venues (Phase 2)
  venueRepRooftopStage:  number;
  venueFanRooftopStage:  number;
  venueSalesArtsCentre:  number;
  venueFanArtsCentre:    number;
  venueRepUnderground:   number;
  venueSalesUnderground: number;
  venueRepTownHall:      number;
  venueFanTownHall:      number;

  // Unit combat — ADMIN: unitDamage
  unitDamage: number;

  // Enemy HP — ADMIN: enemyHpMogul | enemyHpSwarm | enemyHpBadManager | enemyHpTribute | enemyHpMusicPress | enemyHpPirate
  enemyHpMogul:      number;
  enemyHpSwarm:      number;
  enemyHpBadManager: number;
  enemyHpTribute:    number;
  enemyHpMusicPress: number;
  enemyHpPirate:     number;

  // Enemy damage — ADMIN: enemyDamageMogul | ... | enemyDamagePirate
  enemyDamageMogul:      number;
  enemyDamageSwarm:      number;
  enemyDamageBadManager: number;
  enemyDamageTribute:    number;
  enemyDamageMusicPress: number;
  enemyDamagePirate:     number;

  // Enemy movement — ADMIN: enemyMoveSpeedMogul | ... | enemyMoveSpeedPirate
  enemyMoveSpeedMogul:      number;
  enemyMoveSpeedSwarm:      number;
  enemyMoveSpeedBadManager: number;
  enemyMoveSpeedTribute:    number;
  enemyMoveSpeedMusicPress: number;
  enemyMoveSpeedPirate:     number;

  // Enemy combat — ADMIN: enemyAttackRate | enemySightRadius | enemyAttackRadius
  enemyAttackRate:   number;
  enemySightRadius:  number;
  enemyAttackRadius: number;

  // Fog of War — ADMIN: fogExploredAlpha | fogUnexploredAlpha
  fogExploredAlpha:   number;
  fogUnexploredAlpha: number;

  // Good beacon pulses — ADMIN: goodBeaconHealRatio | goodBeaconPulseMin | goodBeaconPulseMax
  goodBeaconHealRatio: number;
  goodBeaconPulseMin:  number;
  goodBeaconPulseMax:  number;

  // Rival beacon — ADMIN: baddieBeaconBuildTime | baddieBeaconHp | baddieBeaconJamming | baddieBeaconPulseTime
  baddieBeaconBuildTime: number;
  baddieBeaconHp:        number;
  baddieBeaconJamming:   number;
  baddieBeaconPulseTime: number;
}

/** The full set of content loaded at boot, from Supabase or bundled defaults. */
export interface GameContent {
  source: 'supabase' | 'defaults';
  balance: BalanceValues;
  spells: SpellDefinition[];
  levels: LevelDefinition[];
  futureSongs: FutureSong[];
}

export interface FutureSong {
  title: string;
  audioUrl?: string;
  audioPath?: string;
  audioMime?: string;
}

// ── Leaderboards & cloud saves ──────────────────────────────────────────
export interface LeaderboardEntry {
  id?: string;
  playerName: string;
  levelId: string;
  score: number;
  waveReached: number;
  createdAt?: string;
}

export interface CloudSaveRecord {
  slot: string;
  state: GameState;
  updatedAt: string;
}
